import calculateImageTopPosition from '@PROJECT_ROOT/assets/desktop/js/search/calculateImageTopPosition';

/**
 * Stvara jednostavnu galeriju na zadanom imageElement objektu.
 *
 * @param {object} document
 * @param {function} $ajax metoda koju će galerija koristiti za poziv ajaxa, npr. $.get ili $.post
 * @param {object} options opcije galerije:
 * * imageElement dom element
 * * source (url string ako koristimo ajax ili array objekata sa "url" property-jem koji se postavlja u img src atribut)
 * * queryParameters objekt sa query parametrima ({ key: value }), ignorira se ako je source tipa array
 * * counterElement element koji će se ažurirati sa indeksom trenutno prikazane slike
 * * nextButton element koji predstavlja botun za prelaz na iduću sliku
 * * previousButton element koji predstavlja botun za prelaz na prijašnju sliku
 * * [optional] galleryHeight visina galerije, ako postoji ovaj element, svaka slika u galeriji ce se pozicionirati u
 * sredinu ovisno o ovoj vrijednosti
 * * [optional] imageDescription element koji drži opis slike
 * * [optional] toggleImageDescription u kombinaciji sa imageDescription elementom, na klik na sliku se togglea prikaz
 * imageDescription elementa
 *
 * @return {object}
 */
function createSimplePrivateGalleryUnconfigured(document, $ajax, options) {
    let imagesPromise;
    let lastRequestErrored = false;
    let images = null;
    let currentImageIndex = options.currentImageIndex ? options.currentImageIndex : 0;
    const afterNewImageDisplayCallbacks = [];
    const onGalleryClickCallbacks = [];
    const loaderClass = options.loaderClass ? options.loaderClass : 'loader32w';
    let imageDescriptionHidden = false;
    const { source, imageElement, imageDescription, counterElement } = options;

    /**
     * Koristimo .getAttribute() umjesto dataset property-ja jer je dataset podržan tek od IE11. Dodatno, dataset
     * mijenja DOM, dok .getAttribute() to ne radi.
     *
     * @see https://developer.mozilla.org/en-US/docs/Learn/HTML/Howto/Use_data_attributes#Issues
     */
    if (process.env.NODE_ENV !== 'production') {
        if (imageElement.getAttribute('data-galleryInstantiated') === 'true') {
            // eslint-disable-next-line no-console
            console.error('Greška: već je stvorena galerija za ovaj image element.');
            return {};
        }
    }
    imageElement.setAttribute('data-galleryInstantiated', 'true');

    const imageLoaderElement = createImageLoader();
    insertImageLoaderAfterImageElement();
    positionImage();

    imageElement.onload = imageOnLoad;
    imageElement.onclick = onGalleryClick;

    if (Array.isArray(source)) {
        updateCounterElement(currentImageIndex, source.length);
    }

    options.nextButton.addEventListener('click', displayNextImage);
    options.previousButton.addEventListener('click', displayPreviousImage);
    imageElement.addEventListener('swipeleft', displayNextImage);
    imageElement.addEventListener('swiperight', displayPreviousImage);

    return {
        // Registrira callback koji će se pokrenuti nakon svakog završenog update-a slike. Može se registrirati više
        // callbacka, pokrenuti će se redom kojim su dodani.
        afterNewImageDisplay(callback) {
            afterNewImageDisplayCallbacks.push(callback);
        },
        onGalleryClick(callback) {
            onGalleryClickCallbacks.push(callback);
        },
    };

    function createImageLoader() {
        const loaderElement = document.createElement('div');
        loaderElement.className = loaderClass;
        loaderElement.style.position = 'absolute';

        return loaderElement;
    }

    function insertImageLoaderAfterImageElement() {
        imageElement.parentNode.insertBefore(imageLoaderElement, imageElement.nextSibling);
    }

    function displayNextImage(event) {
        showLoader();

        fetchImages().then((fetchedImages) => {
            currentImageIndex += 1;
            if (currentImageIndex >= fetchedImages.length) {
                currentImageIndex = 0;
            }

            updateGalleryDisplay(fetchedImages, currentImageIndex, fetchedImages.length, 'next', event);
        });
    }

    function displayPreviousImage(event) {
        showLoader();

        fetchImages().then((fetchedImages) => {
            currentImageIndex -= 1;
            if (currentImageIndex < 0) {
                currentImageIndex = fetchedImages.length - 1;
            }

            updateGalleryDisplay(fetchedImages, currentImageIndex, fetchedImages.length, 'prev', event);
        });
    }

    function toggleImageDescriptionVisibility() {
        if (!imageDescription.textContent) {
            return;
        }

        imageDescription.style.display = imageDescription.style.display === 'none' ? '' : 'none';
        imageDescriptionHidden = !imageDescriptionHidden;
    }

    function fetchImages() {
        return new Promise((resolve) => {
            if (Array.isArray(source)) {
                resolve(source);
                return;
            }

            // Slike su već dohvaćene u nekom od prijašnjih ajax poziva
            if (images !== null) {
                resolve(images);
                return;
            }

            // Za slučaj kada smo vrlo brzo nekoliko puta kliknuli na next/previous botun, a prijašnji request se
            // nije dovršio, pričekat ćemo da taj request završi i uzeti njegove podatke umjesto da vršimo novi ajax
            // poziv.
            if (!imagesPromise || lastRequestErrored) {
                imagesPromise = $ajax(source, options.queryParameters);
            }

            imagesPromise.then((receivedImages) => {
                images = receivedImages;
                resolve(receivedImages);
            }, (errorData) => {
                lastRequestErrored = true;
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.error('Nije uspjelo dohvaćanje slika, ajax greška:', errorData);
                }
                resolve([{ url: imageElement.src, alt: imageElement.alt }]);
            });
        });
    }

    function updateGalleryDisplay(imageList, imageToDisplayIndex, imageTotal, btnClicked, event) {
        const imageToDisplay = imageList[imageToDisplayIndex];
        imageElement.src = imageToDisplay.url;

        imageElement.alt = imageToDisplay.alt
            ? imageToDisplay.alt
            : '';

        imageElement.title = imageToDisplay.title
            ? imageToDisplay.title
            : '';

        updateCounterElement(imageToDisplayIndex, imageTotal);

        if (imageDescription) {
            updateImageCaption(imageToDisplay);
            toggleCounterAndDescriptionDisplay();
        }

        afterNewImageDisplayCallbacks.forEach((callback) => {
            callback(createImageDataForCallback(imageList, imageToDisplayIndex, btnClicked, event));
        });
    }

    function updateCounterElement(imageIndex, imageTotal) {
        counterElement.textContent = `${imageIndex + 1}/${imageTotal}`;
        counterElement.style.display = '';
    }

    function toggleCounterAndDescriptionDisplay() {
        const description = imageDescription;

        if (description.textContent && !imageDescriptionHidden) {
            description.style.display = '';
            counterElement.style.display = 'none';
        } else {
            description.style.display = 'none';
            counterElement.style.display = '';
        }
    }

    function updateImageCaption(image) {
        imageDescription.innerHTML = image.description ? image.description : '';
    }

    function showLoader() {
        imageLoaderElement.style.display = 'block';
    }

    function imageOnLoad() {
        hideLoader();
        positionImage();
    }

    function onGalleryClick() {
        showLoader();
        fetchImages().then((fetchedImages) => {
            if (imageDescription && options.toggleImageDescription) {
                toggleImageDescriptionVisibility();
                toggleCounterAndDescriptionDisplay();
            }

            hideLoader();

            onGalleryClickCallbacks.forEach((callback) => {
                callback(fetchedImages[currentImageIndex]);
            });
        });
    }

    function hideLoader() {
        imageLoaderElement.style.display = 'none';
    }

    function positionImage() {
        if (options.galleryHeight) {
            const topPosition = calculateImageTopPosition(options.galleryHeight, imageElement.height);
            imageElement.style.top = `${topPosition}px`;
        }
    }
}

function createImageDataForCallback(images, currentImageIndex, btnClicked, event) {
    return { ...images[currentImageIndex],
        currentIndex: currentImageIndex + 1,
        totalImages: images.length,
        btnClicked,
        isTriggered: event.originalEvent };
}

export default function createSimplePrivateGallery(options) {
    return createSimplePrivateGalleryUnconfigured(document, $.post, options);
}

function createSimplePrivateGalleryWithAjaxSource($ajax, options) {
    return createSimplePrivateGalleryUnconfigured(document, $ajax, options);
}

export {
    createSimplePrivateGalleryUnconfigured,
    createSimplePrivateGalleryWithAjaxSource,
};
